/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  formatAdmissionDeadline,
  formatMoney,
  isDomainAllowed
} from '@/utils/helperFunctions';
import { Box, Divider, Grid, Typography } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';
import Cookies from 'js-cookie';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useRemoveFavoriteProgramMutation,
  useToggleFavoriteMutation
} from '@/redux/api/programApi';
import { styles } from './styles';

const ProgramCard = ({
  program,
  admission,
  onFavoritePage,
  removeFavorite,
  addAgainIfFailed,
  universityCampusImage,
  programTitle,
  modeOfStudy,
  universityProgramUrl,
  programAdmissionDeadline,
  universityCampusAddress,
  universityLogoUrl,
  isLoading = false
}: {
  program: any;
  admission?: boolean;
  isFavorite?: boolean;
  onFavoritePage?: boolean;
  removeFavorite?: (id: string) => void;
  addAgainIfFailed?: (program: any) => void;
  address?: string;
  universityCampusImage?: string;
  programTitle?: string;
  modeOfStudy?: string;
  universityProgramUrl?: string;
  programAdmissionDeadline?: string;
  universityCampusAddress?: string;
  universityLogoUrl?: string;
  isLoading?: boolean;
}) => {
  const userId = useMemo(() => {
    const user = Cookies.get('userId');
    try {
      return user ?? null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }, []);

  const { isAlreadyFavourite } = useMemo(() => {
    if (!userId || !program?.favouriteBy) {
      return { isAlreadyFavourite: false, otherFavUsers: [] };
    }

    if (onFavoritePage) {
      const favouriteByIds = program.favouriteBy.map(
        (fav: { id: string }) => fav.id
      );
      return {
        isAlreadyFavourite: favouriteByIds.includes(userId),
        otherFavUsers:
          favouriteByIds.filter((id: string) => id !== userId) || []
      };
    } else {
      return {
        isAlreadyFavourite: program.favouriteBy.includes(userId),
        otherFavUsers:
          program.favouriteBy.filter((id: string) => id !== userId) || []
      };
    }
  }, [program?.favouriteBy, userId, onFavoritePage]);

  const [isFavorite, setIsFavorite] = useState(
    isAlreadyFavourite || onFavoritePage
  );
  const [toggleFavorite, { isLoading: isProcessing }] =
    useToggleFavoriteMutation();
  const [removeFavoriteProgram, { isLoading: isRemovingFavorite }] =
    useRemoveFavoriteProgramMutation();

  const { formattedDate, hasPassed } = formatAdmissionDeadline(
    programAdmissionDeadline || program?.admission?.admission_deadline
  );

  const { city, country } =
    (onFavoritePage
      ? program?.admission?.campus_id?.address_id
      : program?.admission?.campus?.address) || {};
  const logo = onFavoritePage
    ? program?.admission?.university_id?.logo_url
    : program?.admission?.university?.logo_url;

  const campusImage = onFavoritePage
    ? program?.admission?.campus_id?.logo_url
    : program?.admission?.campus?.logo_url;

  const programUrl =
    universityProgramUrl && universityProgramUrl.trim() !== ''
      ? universityProgramUrl
      : program?._id && program?.program?._id
        ? `/program-details/${program._id}?program_id=${program.program._id}`
        : undefined;

  const isProcessingOrRemoving = isProcessing || isRemovingFavorite;

  const handleFavorite = useCallback(
    async (e: { preventDefault: () => void; stopPropagation: () => void }) => {
      e.preventDefault();
      e.stopPropagation();

      const token = Cookies.get('accessToken');

      if (!token || !userId) {
        const loginUrl = `/login?redirect=${encodeURIComponent('/programs')}`;
        window.location.href = loginUrl;
        return;
      }

      if (isProcessingOrRemoving) {
        return;
      }

      try {
        const newFavoriteState = !isFavorite;
        setIsFavorite(newFavoriteState);

        if (isFavorite && removeFavorite) {
          removeFavorite(program?._id || program?.id);
        }

        if (isFavorite) {
          await removeFavoriteProgram({
            admissionProgramId: program?._id || program?.id
          }).unwrap();
        } else {
          await toggleFavorite({
            admissionProgramId: program._id || program.id,
            userIds: [userId]
          }).unwrap();
        }

        // toast.success(
        //   newFavoriteState ? 'Added to favorites' : 'Removed from favorites'
        // );
      } catch (error) {
        setIsFavorite(isFavorite);
        if (onFavoritePage && isFavorite && addAgainIfFailed) {
          addAgainIfFailed(program);
        }
        console.error('Error toggling favorite:', error);
        if ((error as { status?: number })?.status === 401) {
          toast.error('Please login to favorite programs');
        } else {
          toast.error('Failed to update favorite status');
        }
      }
    },
    [
      userId,
      isProcessingOrRemoving,
      isFavorite,
      removeFavorite,
      onFavoritePage,
      program,
      removeFavoriteProgram,
      toggleFavorite,
      addAgainIfFailed
    ]
  );

  const favoriteButton = useMemo(
    () => (
      <>
        {userId ? (
          <Box
            onClick={isProcessingOrRemoving ? undefined : handleFavorite}
            sx={styles.favoriteButton(isProcessingOrRemoving)}
          >
            <Image
              src={`/assets/svg/${isFavorite ? 'heart' : 'heart-outlined'}.svg`}
              alt="favorite"
              width={24}
              height={24}
              style={styles.favoriteIcon(isProcessingOrRemoving)}
            />
          </Box>
        ) : null}
      </>
    ),
    [userId, isProcessingOrRemoving, handleFavorite, isFavorite]
  );

  return (
    <Box sx={styles.container(isProcessingOrRemoving || isLoading)}>
      {typeof programUrl === 'string' && programUrl.trim().length > 0 && (
        <Box sx={styles.card}>
          <Grid container spacing={2}>
            <Grid
              sx={styles.imageGridContainer(admission ? true : false)}
              size={{
                xs: 12,
                sm: 5,
                md: 4
              }}
            >
              <Image
                src={
                  isDomainAllowed(universityCampusImage || campusImage)
                    ? universityCampusImage || campusImage
                    : '/assets/png/university_placeholder.png'
                }
                alt="programImage"
                width={admission ? 224 : 332}
                height={admission ? 209 : 251}
                style={
                  {
                    ...styles.programImage,
                    width: '100%'
                  } as React.CSSProperties
                }
                priority
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: admission ? 12 : 7,
                md: 8
              }}
            >
              <Box sx={styles.cardInfo}>
                <Box sx={styles.cardDetails}>
                  <Box>
                    <Box sx={styles.headerContainer}>
                      {admission && (
                        <Box sx={styles.responsiveImageContainer}>
                          <Image
                            src={
                              isDomainAllowed(campusImage)
                                ? campusImage
                                : '/assets/png/university_placeholder.png'
                            }
                            alt="programImage"
                            width={500}
                            height={150}
                            style={{
                              alignSelf: 'center',
                              width: '100%',
                              objectFit: 'cover',
                              borderRadius: '12px'
                            }}
                            priority
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          />
                        </Box>
                      )}
                      <Box sx={styles.titleContainer}>
                        <Typography
                          variant="h5"
                          fontWeight="600"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.2,
                            maxHeight: '2.4em'
                          }}
                        >
                          {programTitle || program?.program?.name || ''}
                        </Typography>
                        {favoriteButton}
                      </Box>
                    </Box>
                    <Box sx={styles.infoRow}>
                      <Image
                        src={universityLogoUrl || logo}
                        alt="university logo"
                        width={60}
                        height={40}
                        style={{
                          width: 'auto',
                          maxWidth: '120px',
                          height: '60px',
                          objectFit: 'contain'
                        }}
                        priority
                      />
                      <Box sx={styles.locationContainer}>
                        <Image
                          src="/assets/svg/location.svg"
                          alt="location"
                          width={24}
                          height={24}
                          priority
                        />
                        <Typography variant="body1">
                          {universityCampusAddress ||
                            `${city || '_'} ${country || '_'}`}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box gap={1} sx={styles.programDetails}>
                    <Box sx={styles.detailRow}>
                      <Image
                        src={`/assets/svg/${hasPassed ? 'calendar' : 'calendar-primary'}.svg`}
                        alt="calendar"
                        width={24}
                        height={24}
                        priority
                      />
                      <Box>
                        <Typography
                          variant="body2"
                          sx={styles.statsText}
                          color={hasPassed ? 'error' : 'primary.main'}
                        >
                          Deadline
                        </Typography>
                        <Typography
                          textAlign="center"
                          variant="body1"
                          fontWeight="600"
                          sx={styles.statsText}
                          color={hasPassed ? 'error' : 'primary.main'}
                        >
                          {formattedDate || ''}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={styles.divider}
                    />
                    <Box sx={styles.detailRow}>
                      <Image
                        src="/assets/svg/fee-icon.svg"
                        alt="fee icon"
                        width={24}
                        height={24}
                        priority
                      />
                      <Box>
                        <Typography sx={styles.statsText} variant="body2">
                          Fee (Semester)
                        </Typography>
                        <Typography
                          sx={styles.statsText}
                          variant="body1"
                          fontWeight="600"
                        >
                          {onFavoritePage
                            ? program?.program?.fee_structure?.tuition_fee
                              ? formatMoney(
                                  Number(
                                    program?.program?.fee_structure?.tuition_fee
                                  )
                                )
                              : 'Will Update Soon'
                            : program?.fee_structure?.tuition_fee
                              ? formatMoney(
                                  Number(program?.fee_structure?.tuition_fee)
                                )
                              : 'Will Update Soon'}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={styles.divider}
                    />
                    <Box sx={styles.detailRow}>
                      <Image
                        src="/assets/svg/teacher.svg"
                        alt="teacher"
                        width={24}
                        height={24}
                        priority
                      />
                      <Box>
                        <Typography sx={styles.statsText} variant="body2">
                          Study Mode
                        </Typography>
                        <Typography
                          sx={styles.statsText}
                          variant="body1"
                          fontWeight="600"
                        >
                          {modeOfStudy || program?.program?.mode_of_study}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Link
                    href={
                      isLoading || isProcessingOrRemoving ? '#' : programUrl
                    }
                    prefetch={true}
                    style={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block',
                      width: '100%',
                      pointerEvents: isProcessingOrRemoving ? 'none' : 'auto'
                    }}
                    onClick={(e) => {
                      if (isLoading || isProcessingOrRemoving) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  >
                    <Box className="view-program" sx={styles.viewProgramButton}>
                      View Program
                    </Box>
                  </Link>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default ProgramCard;
